import React, { useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import moment from "moment/moment";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Title,
  Legend,
} from "chart.js";
import useFetch from "../../../../../hooks/fetchMSSQL-hook";
import validFilter from "../../utils/validFilter";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Title,
  Legend
);

const getChartConfig = () => {
  return {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 5,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: (context) => {
            const value = context.raw || 0;
            const count = context.dataset.countValue[context.dataIndex] || 0;
            const formattedValue = new Intl.NumberFormat("pl-PL", {
              style: "currency",
              currency: "PLN",
            }).format(value);
            return `${context.dataset.label}: ${formattedValue} (${count} szt.)`;
          },
          title: (context) => {
            const ctxIdx = context[0].dataIndex;
            const date = context[0].dataset.dateTime[ctxIdx];
            const m = moment(date);
            return `${m.format("YYYY-MM-DD (dddd)")}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => {
            return new Intl.NumberFormat("pl-PL", {
              style: "currency",
              currency: "PLN",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value);
          },
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
    },
  };
};

export default function SalesByDayChart({ machines }) {
  const chartRef = useRef(null);
  const { fetchMssqlApi } = useFetch();
  const [dateRange, setDateRange] = useState("last7Days");
  const [salesData, setSalesData] = useState([]);
  const [machineFilter, setMachineFilter] = useState("");

  const getDateRange = (range) => {
    const now = moment();
    switch (range) {
      case "thisWeek":
        return {
          from: now.clone().startOf("week").format("YYYY-MM-DD"),
          to: now.format("YYYY-MM-DD"),
        };
      case "last7Days":
        return {
          from: now.clone().subtract(6, "days").format("YYYY-MM-DD"),
          to: now.format("YYYY-MM-DD"),
        };
      case "previousWeek":
        return {
          from: now
            .clone()
            .subtract(1, "week")
            .startOf("week")
            .format("YYYY-MM-DD"),
          to: now
            .clone()
            .subtract(1, "week")
            .endOf("week")
            .format("YYYY-MM-DD"),
        };
      case "thisMonth":
        return {
          from: now.clone().startOf("month").format("YYYY-MM-DD"),
          to: now.format("YYYY-MM-DD"),
        };
      case "last30Days":
        return {
          from: now.clone().subtract(29, "days").format("YYYY-MM-DD"),
          to: now.format("YYYY-MM-DD"),
        };
      case "previousMonth":
        return {
          from: now
            .clone()
            .subtract(1, "month")
            .startOf("month")
            .format("YYYY-MM-DD"),
          to: now
            .clone()
            .subtract(1, "month")
            .endOf("month")
            .format("YYYY-MM-DD"),
        };
      default:
        return {
          from: now.clone().startOf("week").format("YYYY-MM-DD"),
          to: now.format("YYYY-MM-DD"),
        };
    }
  };

  const getData = async (range) => {
    const { from, to } = getDateRange(range);
    fetchMssqlApi(
      "stat/period-sales",
      {
        method: "POST",
        data: {
          fromCreateDateTime: from,
          toCreateDateTime: to,
        },
        hideNotification: true,
      },
      (data) => setSalesData(data),
      (err) => console.error(err)
    );
  };

  const getMachineSerialNo = ({ machineName }) => {
    if (!machineName || !machines.length) return null;

    const machine = machines.find(
      ({ Name, SerialNo }) => `${Name} (${SerialNo})` === machineName
    );
    return machine?.SerialNo || null;
  };

  const handleChangeMachine = (evt) => {
    const machineName = (evt.target && evt.target.value) || "";
    setMachineFilter(machineName);

    const serialNo = getMachineSerialNo({ machineName });

    if (serialNo) {
      validFilter({ machineName }).then(() => {
        const { from, to } = getDateRange(dateRange);
        fetchMssqlApi(
          "stat/period-sales",
          {
            method: "POST",
            data: {
              fromCreateDateTime: from,
              toCreateDateTime: to,
              machineSerialNo: serialNo,
            },
            hideNotification: true,
          },
          (data) => {
            setSalesData(data);
          },
          (err) => {
            console.log(err);
          }
        );
      });
    }
  };

  useEffect(() => {
    getData(dateRange);
  }, [dateRange]);

  const chartData = {
    labels: salesData.map((item) => {
      const m = moment(item.Date);
      return `${m.format("DD.MM")}`;
    }),
    datasets: [
      {
        label: "Sprzedaż",
        data: salesData.map((item) => item.Value),
        countValue: salesData.map((item) => item.Count),
        dateTime: salesData.map((item) => item.Date),
        borderColor: "#0140BE",
        backgroundColor: "#0140be80",
        tension: 0.4,
      },
    ],
  };

  return (
    <div>
      <div name="filter" className="row">
        <div className="col-lg-8 col-md-12">
          <div className="d-flex justify-content-between justify-content-center h-100">
            <div className="d-flex align-items-center mr-2">
              <input
                type="radio"
                className="btn-check mr-2"
                onClick={() => setDateRange("thisWeek")}
                name="dateRange"
                value="thisWeek"
                id="dateRange-thisWeek"
                checked={dateRange === "thisWeek"}
              />
              <label htmlFor="dateRange-thisWeek" className="mb-0">
                Ten tydzień
              </label>
            </div>
            <div className="d-flex align-items-center mr-2">
              <input
                type="radio"
                className="btn-check mr-2"
                onClick={() => setDateRange("last7Days")}
                name="dateRange"
                value="last7Days"
                id="dateRange-last7Days"
                checked={dateRange === "last7Days"}
              />
              <label htmlFor="dateRange-last7Days" className="mb-0">
                Ostatnie 7 dni
              </label>
            </div>
            <div className="d-flex align-items-center mr-2">
              <input
                type="radio"
                className="btn-check mr-2"
                onClick={() => setDateRange("previousWeek")}
                name="dateRange"
                value="previousWeek"
                id="dateRange-previousWeek"
                checked={dateRange === "previousWeek"}
              />
              <label htmlFor="dateRange-previousWeek" className="mb-0">
                Poprzedni tydzień
              </label>
            </div>
            <div className="d-flex align-items-center mr-2">
              <input
                type="radio"
                className="btn-check mr-2"
                onClick={() => setDateRange("thisMonth")}
                name="dateRange"
                value="thisMonth"
                id="dateRange-thisMonth"
                checked={dateRange === "thisMonth"}
              />
              <label htmlFor="dateRange-thisMonth" className="mb-0">
                Ten miesiąc
              </label>
            </div>
            <div className="d-flex align-items-center mr-2">
              <input
                type="radio"
                className="btn-check mr-2"
                onClick={() => setDateRange("last30Days")}
                name="dateRange"
                value="last30Days"
                id="dateRange-last30Days"
                checked={dateRange === "last30Days"}
              />
              <label htmlFor="dateRange-last30Days" className="mb-0">
                Ostatnie 30 dni
              </label>
            </div>
            <div className="d-flex align-items-center mr-2">
              <input
                type="radio"
                className="btn-check mr-2"
                onClick={() => setDateRange("previousMonth")}
                name="dateRange"
                value="previousMonth"
                id="dateRange-previousMonth"
                checked={dateRange === "previousMonth"}
              />
              <label htmlFor="dateRange-previousMonth" className="mb-0">
                Poprzedni miesiąc
              </label>
            </div>
          </div>
        </div>
          <div className="col-lg-4 col-md-12">
            <input
              className="form-control"
              placeholder="Automat"
              list="machines"
              name="machine"
              onChange={handleChangeMachine}
              autoComplete="off"
              defaultValue={machineFilter}
              onFocus={(e) => {
                e.target.value = "";
                handleChangeMachine("");
              }}
              onBlur={(e) => !e.target.value && getData()}
            />
            <datalist id="machines">
              {machines &&
                machines.map((machine, idx) => (
                  <option
                    value={`${machine.Name} (${machine.SerialNo})`}
                    key={idx}
                  />
                ))}
            </datalist>
          </div>
      </div>

      <Line ref={chartRef} data={chartData} options={getChartConfig()} />
    </div>
  );
}
